import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { PlanningTemplateModal } from "@components/modals/PlanningTemplateModal";
import SearchField from "@components/ui/textfields/SearchField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState, useCallback } from "react";
import { planningTemplatesApi } from "@services/api";
import { showNotification } from "context/notification.service";
import { defaultPlanningTemplates } from "./defaultPlanningTemplates";

export const PlanningTemplates = () => {
  const planningTemplates = useProjectStore((state) => state.planningTemplates);
  const userData = useDataStore((state) => state.userData);
  const [value, setValue] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);

  const handleCreateDefaults = async () => {
    if (!userData?.org_id) return;

    setIsCreatingDefaults(true);
    try {
      let created = 0;
      for (const template of defaultPlanningTemplates) {
        const result = await planningTemplatesApi.create({
          name: template.name,
          org_id: userData.org_id,
          model: template.model,
          sections: template.sections,
        });
        useProjectStore.getState().addPlanningTemplate(result);
        created++;
      }
      showNotification({
        type: "success",
        message: `${created} modèle(s) par défaut ajouté(s)`,
      });
    } catch (error) {
      console.error("Error creating default planning templates:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la création des modèles",
      });
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  const sortedTemplates = [...planningTemplates].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const handleReorder = useCallback(async (templateId: string, direction: "up" | "down") => {
    const sorted = [...planningTemplates].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const idx = sorted.findIndex((t) => t.id === templateId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const updates: { id: string; order: number }[] = sorted.map((t, i) => ({ id: t.id, order: i }));
    const tmp = updates[idx].order;
    updates[idx].order = updates[swapIdx].order;
    updates[swapIdx].order = tmp;

    // Optimistic update
    const updated = planningTemplates.map((t) => {
      const u = updates.find((u) => u.id === t.id);
      return u ? { ...t, order: u.order } : t;
    });
    useProjectStore.getState().setPlanningTemplates(updated);

    for (const u of updates.filter((_, i) => i === idx || i === swapIdx)) {
      planningTemplatesApi.update(u.id, { order: u.order }).catch(() => {});
    }
  }, [planningTemplates]);

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
      {(selectedTemplate || newTemplateOpen) && (
        <PlanningTemplateModal
          templateId={selectedTemplate || ""}
          onClose={() => {
            setSelectedTemplate(null);
            setNewTemplateOpen(false);
          }}
        />
      )}
      <div className="flex flex-col w-full max-w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Modèles plannings</Text1>
          <Text2>Configurez vos modèles de planning ici.</Text2>
          <Text3>
            Les modèles de planning définissent les semaines pré-remplies par
            défaut pour chaque étape lors de la création d'un planning.
          </Text3>
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-full max-w-400">
            <SearchField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Rechercher un modèle..."
            />
          </div>
          {planningTemplates.length === 0 && (
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              onClick={handleCreateDefaults}
              disabled={isCreatingDefaults}
            >
              {isCreatingDefaults
                ? "Création en cours..."
                : "Ajouter les modèles par défaut"}
            </SecondaryButton>
          )}
        </div>

        <div className="flex flex-col divide-y mt-4">
          {sortedTemplates
            .filter((template) =>
              template.name.toLowerCase().includes(value.toLowerCase()),
            )
            .map((template, index) => {
              const sectionCount = template.sections?.length || 0;
              return (
                <div
                  key={template.id}
                  className="flex flex-row rounded-xl items-center px-4 py-2"
                >
                  <div className="flex flex-col gap-0.5 mr-3">
                    <button
                      className="p-0.5 rounded hover:bg-neutral-100 text-neutral-300 hover:text-neutral-600 disabled:opacity-20"
                      onClick={() => handleReorder(template.id, "up")}
                      disabled={index === 0}
                    >
                      <Icon1 name="up2" size={14} />
                    </button>
                    <button
                      className="p-0.5 rounded hover:bg-neutral-100 text-neutral-300 hover:text-neutral-600 disabled:opacity-20"
                      onClick={() => handleReorder(template.id, "down")}
                      disabled={index === sortedTemplates.length - 1}
                    >
                      <Icon1 name="down2" size={14} />
                    </button>
                  </div>
                  <div className="flex flex-col w-250">
                    <div className="text-h2 font-medium">{template.name}</div>
                  </div>
                  <div className="text-h3 text-neutral-400 mr-auto flex flex-row gap-3">
                    <span>{template.model.replace(/_/g, " ")}</span>
                    <span>{sectionCount} sections</span>
                  </div>
                  <SecondaryButton
                    label="Modifier"
                    variant="small"
                    onClick={() => setSelectedTemplate(template.id)}
                  />
                </div>
              );
            })}
        </div>

        <Button
          className="mt-4"
          label="Nouveau modèle de planning"
          onClick={() => setNewTemplateOpen(true)}
        />
      </div>
    </div>
  );
};
